import React, {useState, useEffect} from "react";
import {
    Box,
    Card,
    CardContent,
    Grid,
    Typography,
    Select,
    MenuItem,
    FormControl,
    Divider,
    IconButton,
    useTheme,
    alpha,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    Switch,
    useMediaQuery,
    FormHelperText
} from "@mui/material";
import {CircularProgressbar, buildStyles} from "react-circular-progressbar";
import "react-circular-progressbar/dist/styles.css";
import CryptoJS from "crypto-js";
import {KEY, ApiDetails} from "../dummyData";

// Icons
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import PeopleAltIcon from '@mui/icons-material/PeopleAlt';
import MeetingRoomIcon from '@mui/icons-material/MeetingRoom';
import PercentIcon from '@mui/icons-material/Percent';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import LoadingButton from '@mui/lab/LoadingButton';
// import WaterDropIcon from '@mui/icons-material/WaterDrop';
import ElectricBoltIcon from '@mui/icons-material/ElectricBolt';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import InfoIcon from '@mui/icons-material/Info';
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDateFns} from "@mui/x-date-pickers/AdapterDateFns";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
// import { showSnackbar } from "../../../../components/SnackBar/SnackBar";
import {showSnackbar} from "./SnackBar/SnackBar";
import {DataGrid} from "@mui/x-data-grid";
import {styled} from "@mui/system";
import WaterDropIcon from "@mui/icons-material/Water";
import {ErrorMessage, Field, Form, Formik} from "formik";
import CircularProgress from "@mui/material/CircularProgress";
import CardBillingConfirmationDialog from "../pages/tenant/tenant components/cardBillingConfirmatinDialog";
import {getMonthName} from "../pages/property/utils/helpers";

// Styled DataGrid for billing
const StyledDataGrid = styled(DataGrid)(({theme}) => ({
    "& .MuiDataGrid-root": {
        minHeight: "200px", // Minimum height for empty states
        maxHeight: "600px", // Maximum height to prevent excessive growth
        height: "auto", // Auto height based on content
        overflowY: "auto",  // Enables scrolling when needed
    },
    "& .MuiDataGrid-columnHeaders": {
        fontSize: "1rem",
        fontWeight: 700,
        backgroundColor: "#E3F2FD", // Light blue background
        color: "#0D47A1", // Dark blue text
    },
    "& .MuiDataGrid-row:hover": {
        backgroundColor: "#f5faf5", // Light green hover color
    },
    "& .MuiDataGrid-cell:focus": {
        outline: "none",
    },
    "& .MuiDataGrid-cell": {
        fontSize: "0.9rem",
    },
}));

// Custom progress circle component
const ProgressCircle = ({value, text, label, description, icon, color, size = 180}) => {
    const theme = useTheme();
    const safeValue = isNaN(value) ? 0 : value;
    const formattedValue = safeValue.toFixed(1);

    return (
        <Box sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            p: 2,
            borderRadius: 2,
            transition: 'transform 0.3s, box-shadow 0.3s',
            '&:hover': {
                transform: 'translateY(-5px)',
                boxShadow: '0 8px 16px rgba(0,0,0,0.1)'
            }
        }}>
            <Box sx={{position: 'relative', width: size, height: size}}>
                <CircularProgressbar
                    value={safeValue}
                    text={`${formattedValue}%`}
                    styles={buildStyles({
                        pathColor: color,
                        textColor: theme.palette.text.primary,
                        trailColor: alpha(color, 0.15),
                        textSize: '20px',
                        pathTransitionDuration: 1.5,
                    })}
                />
                <Box
                    sx={{
                        position: 'absolute',
                        top: -20,
                        right: -10,
                        backgroundColor: alpha(color, 0.1),
                        borderRadius: '50%',
                        p: 1,
                        color: color,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        boxShadow: `0 4px 8px ${alpha(color, 0.2)}`
                    }}
                >
                    {icon}
                </Box>
            </Box>
            <Typography variant="h6" fontWeight="bold" sx={{mt: 2, textAlign: 'center'}}>
                {label}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{textAlign: 'center', mt: 0.5}}>
                {description}
            </Typography>
        </Box>
    );
};

// Deposit Management Component
const DepositManagement = ({open, onClose, onSave}) => {
    const theme = useTheme();
    const [depositItems, setDepositItems] = useState([{description: '', amount: '', refundable: ''}]);
    const [totalDeposit, setTotalDeposit] = useState(0);
    const [refundableDeposit, setRefundableDeposit] = useState(0);
    const [depositSet, setDepositSet] = useState(false);

    const addDepositItem = () => {
        setDepositItems([...depositItems, {description: '', amount: '', refundable: ''}]);
    };

    const updateDepositItem = (index, field, value) => {
        const updatedItems = [...depositItems];
        updatedItems[index][field] = value;
        setDepositItems(updatedItems);
        setDepositSet(false);
    };

    const removeDepositItem = (index) => {
        if (depositItems.length > 1) {
            const updatedItems = depositItems.filter((_, i) => i !== index);
            setDepositItems(updatedItems);
            setDepositSet(false);
        }
    };

    const calculateDeposit = () => {
        try {
            let total = 0;
            let refundable = 0;

            depositItems.forEach(item => {
                if (!item.description || !item.amount || !item.refundable) {
                    throw new Error("Please fill in all deposit fields");
                }

                if (item.refundable.toUpperCase() !== "YES" && item.refundable.toUpperCase() !== "NO") {
                    throw new Error("Refundable field must be YES or NO");
                }

                const amount = parseFloat(item.amount);
                total += amount;

                if (item.refundable.toUpperCase() === "YES") {
                    refundable += amount;
                }
            });

            setTotalDeposit(total);
            setRefundableDeposit(refundable);
            setDepositSet(true);
        } catch (error) {
            showSnackbar({
                title: "Error!",
                message: error.message,
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
        }
    };

    const handleSave = () => {
        if (!depositSet) {
            showSnackbar({
                title: "Error!",
                message: "Please calculate deposit first",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        onSave({
            items: depositItems,
            totalDeposit,
            refundableDeposit
        });
        onClose();
    };

    const resetForm = () => {
        setDepositItems([{description: '', amount: '', refundable: ''}]);
        setTotalDeposit(0);
        setRefundableDeposit(0);
        setDepositSet(false);
    };

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle sx={{
                backgroundColor: "#2C5283",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between"
            }}>
                <Typography variant="h6">Add Deposit Details</Typography>
                <IconButton onClick={onClose} sx={{color: "white"}}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{p: 3}}>
                <Box sx={{display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3}}>
                    <Typography variant="h6" fontWeight="bold">Deposit Items</Typography>
                    <Button
                        onClick={addDepositItem}
                        variant="contained"
                        size="small"
                        sx={{
                            background: `linear-gradient(45deg, #2C5283, #1E3A63)`,
                            textTransform: "none"
                        }}
                    >
                        Add Item
                    </Button>
                </Box>

                {depositItems.map((item, index) => (
                    <Box key={index} sx={{
                        mb: 3,
                        p: 2,
                        border: '1px solid #e0e0e0',
                        borderRadius: 2,
                        backgroundColor: '#f9f9f9'
                    }}>
                        <Box sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2}}>
                            <Typography variant="subtitle1" fontWeight="medium">
                                Item {index + 1}
                            </Typography>
                            {depositItems.length > 1 && (
                                <Button
                                    onClick={() => removeDepositItem(index)}
                                    size="small"
                                    color="error"
                                    sx={{textTransform: "none"}}
                                >
                                    Remove
                                </Button>
                            )}
                        </Box>

                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Description"
                                    value={item.description}
                                    onChange={(e) => updateDepositItem(index, 'description', e.target.value)}
                                    size="small"
                                    placeholder="e.g., House Deposit"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <TextField
                                    fullWidth
                                    label="Amount (KSH)"
                                    type="number"
                                    value={item.amount}
                                    onChange={(e) => updateDepositItem(index, 'amount', e.target.value)}
                                    size="small"
                                    placeholder="0"
                                />
                            </Grid>
                            <Grid item xs={12} sm={4}>
                                <FormControl fullWidth size="small">
                                    <Select
                                        value={item.refundable}
                                        onChange={(e) => updateDepositItem(index, 'refundable', e.target.value)}
                                        displayEmpty
                                    >
                                        <MenuItem value="">Select Refundable</MenuItem>
                                        <MenuItem value="YES">YES - Refundable</MenuItem>
                                        <MenuItem value="NO">NO - Non-refundable</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>
                    </Box>
                ))}

                <Box sx={{mt: 3, mb: 2}}>
                    <Button
                        onClick={calculateDeposit}
                        variant="outlined"
                        fullWidth
                        sx={{
                            borderColor: "#2C5283",
                            color: "#2C5283",
                            '&:hover': {
                                borderColor: "#1E3A63",
                                backgroundColor: alpha("#2C5283", 0.04)
                            }
                        }}
                    >
                        Calculate Deposit
                    </Button>
                </Box>

                {depositSet && (
                    <Box sx={{
                        p: 3,
                        backgroundColor: alpha("#4CAF50", 0.1),
                        borderRadius: 2,
                        border: "1px solid #4CAF50"
                    }}>
                        <Typography variant="h6" fontWeight="bold" sx={{mb: 2, color: "#2E7D32"}}>
                            Deposit Summary
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Total Deposit</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {totalDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                            <Grid item xs={6}>
                                <Typography variant="subtitle2" color="text.secondary">Refundable Amount</Typography>
                                <Typography variant="h5" fontWeight="bold" color="success.main">
                                    KSH {refundableDeposit.toLocaleString()}
                                </Typography>
                            </Grid>
                        </Grid>

                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                Non-refundable: KSH {(totalDeposit - refundableDeposit).toLocaleString()}
                            </Typography>
                        </Box>
                    </Box>
                )}
            </DialogContent>

            <DialogActions sx={{p: 3}}>
                <Button onClick={onClose} color="inherit">Cancel</Button>
                <Button
                    onClick={handleSave}
                    variant="contained"
                    disabled={!depositSet}
                    sx={{
                        background: `linear-gradient(45deg, #4CAF50, #2E7D32)`,
                        '&:disabled': {
                            backgroundColor: '#ccc'
                        }
                    }}
                >
                    Save Deposit
                </Button>
            </DialogActions>
        </Dialog>
    );
};

// Add Tenant Dialog Component
const AddTenantDialog = ({open, onClose, selectedProperty}) => {
    const theme = useTheme();
    const matches = useMediaQuery("(max-width:700px)");
    const [loading, setLoading] = useState(false);
    const [depositModalOpen, setDepositModalOpen] = useState(false);
    const [units, setUnits] = useState([]);
    const [selectedUnit, setSelectedUnit] = useState();
    const [waterReading, setWaterReading] = useState('');
    const [electricityReading, setElectricityReading] = useState('');
    const [depositDetails, setDepositDetails] = useState(null);
    const [depositAsArrears, setDepositAsArrears] = useState(true);
    const [rentAsArrears, setRentAsArrears] = useState(true);
    const [tenantPhoto] = useState(null);
    const [photoOfNationalID] = useState(null);
    const [copyOfContract] = useState(null);
    const [inventoryChecklist] = useState(null);


    const [formData, setFormData] = useState({
            first_name: '',
            last_name: '',
            phone_number: '',
            secondary_phone_number: '',
            national_id: '',
            rent: '',
            next_of_kin_first_name: '',
            next_of_kin_last_name: '',
            next_of_kin_relationship: '',
            next_of_kin_contacts: '',
            next_of_kin_national_id: '',
            occupation_or_profession: "",
            institution_name: "",
            place_of_work: "",
            reason_for_relocating: "",
            previous_residence: "",
            email_address: "",

        })
    ;


    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    const originalText = safeDecrypt("myMap");

    useEffect(() => {
        setUnits([]);
        if (selectedProperty?.id) {
            fetchUnits(selectedProperty.id);
        }
    }, [selectedProperty]);

    const fetchUnits = (propertyId) => {
        fetch(ApiDetails + "pegasus/visionary/property/getUnits", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({property_id: propertyId}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setUnits(data);
                }
            })
            .catch((err) => console.log(err));
    };

    const handleInputChange = (field) => (event) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value
        }));
    };

    const handleUnitChange = (event) => {
        const unit = event.target.value;
        setSelectedUnit(unit);
        setWaterReading(unit.meter_reading);
        setElectricityReading(unit.electricity_meter_reading);
    };

    const handleDepositSave = (deposit) => {
        setDepositDetails(deposit);
        setDepositModalOpen(false);
    };

    const handleSubmit = () => {
        setLoading(true);

        // Validation
        if (!formData.first_name || !formData.last_name) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "First and last names are required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!formData.phone_number || formData.phone_number.length < 10) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Enter a valid phone number",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!selectedUnit) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please select a unit",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!formData.rent) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Rent amount is required",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        if (!depositDetails) {
            setLoading(false);
            showSnackbar({
                title: "Error!",
                message: "Please add deposit details",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }

        // Prepare submission data
        const submitData = {
            ...formData,
            rent_as_arrear: rentAsArrears,
            deposit_as_arrears: depositAsArrears,
            is_student: "NO",
            email_address: "",
            landlord_email_address: JSON.parse(originalText)[5][1],
            landlord_id: JSON.parse(originalText)[0][1],
            property_name: selectedProperty.property_name,
            property_id: selectedProperty.id,
            house_number: selectedUnit.unit,
            meter_reading: waterReading,
            electricity_meter_reading: electricityReading,
            deposit: depositDetails.items.map(item => [item.description, item.amount, item.refundable]),
            deposit_held: depositDetails.refundableDeposit,
        };

        let formDataSubmit = new FormData();
        formDataSubmit.append("body", JSON.stringify(submitData));
        formDataSubmit.append("photo_of_national_id", photoOfNationalID);
        formDataSubmit.append("copy_of_contract", copyOfContract);
        formDataSubmit.append("inventory_checklist", inventoryChecklist);
        formDataSubmit.append("tenant_photo", tenantPhoto);

        fetch(ApiDetails + "pegasus/visionary/tenant/add/tenant", {
            method: "POST",
            body: formDataSubmit,
        })
            .then(async (response) => {
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "Tenant added successfully.",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                    onClose();
                    window.location.reload();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Something went wrong. Please try again later",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
                setLoading(false);
            })
            .catch((err) => {
                setLoading(false);
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong. Please try again later",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };


    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.primary.main, 0.1)}, ${alpha(theme.palette.primary.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.primary.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.primary.main
                                }}
                            >
                                <PersonAddIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Add New Tenant
                                </Typography>
                                {selectedProperty && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedProperty.property_name} • {selectedProperty.location}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{pt: 3, zoom: matches ? "85%" : "100%"}}>
                    <Grid container spacing={3}>
                        {/* Personal Details */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2}}>Personal Details</Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="First Name"
                                value={formData.first_name}
                                onChange={handleInputChange('first_name')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Last Name"
                                value={formData.last_name}
                                onChange={handleInputChange('last_name')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Phone Number"
                                value={formData.phone_number}
                                onChange={handleInputChange('phone_number')}
                                required
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Secondary Phone"
                                value={formData.secondary_phone_number}
                                onChange={handleInputChange('secondary_phone_number')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="National ID"
                                value={formData.national_id}
                                onChange={handleInputChange('national_id')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Monthly Rent (KSH)"
                                type="number"
                                value={formData.rent}
                                onChange={handleInputChange('rent')}
                                required
                            />
                        </Grid>

                        {/* Property Details */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2, mt: 2}}>Property Details</Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <FormControl fullWidth required>
                                <Select
                                    value={selectedUnit}
                                    onChange={handleUnitChange}
                                    displayEmpty
                                    disabled={!units || units.length === 0}
                                >
                                    <MenuItem value="" disabled>Select Unit</MenuItem>
                                    {units && units.length > 0 ? (
                                        units.map((unit, index) => (
                                            <MenuItem
                                                key={index}
                                                value={unit}
                                                disabled={unit.state === "OCCUPIED"}
                                            >
                                                {unit.unit}
                                                <Box component="span" sx={{
                                                    ml: 1,
                                                    color: unit.state === "OCCUPIED" ? "error.main" : "success.main",
                                                    fontSize: "0.8rem"
                                                }}>
                                                    ({unit.state.toLowerCase()})
                                                </Box>
                                            </MenuItem>
                                        ))
                                    ) : (
                                        <MenuItem disabled>No units available</MenuItem>
                                    )}
                                </Select>
                                <FormHelperText>
                                    {!units || units.length === 0 ? "No available units" : "Required"}
                                </FormHelperText>
                            </FormControl>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Button
                                fullWidth
                                variant="outlined"
                                onClick={() => setDepositModalOpen(true)}
                                sx={{
                                    height: 56,
                                    textTransform: "none",
                                    backgroundColor: depositDetails ? alpha(theme.palette.success.main, 0.1) : "#FBF4F0",
                                    borderColor: depositDetails ? theme.palette.success.main : theme.palette.divider,
                                    color: depositDetails ? theme.palette.success.main : theme.palette.text.primary
                                }}
                            >
                                {depositDetails ? `Deposit Set: KSH ${depositDetails.totalDeposit.toLocaleString()}` : "Add Deposit Details"}
                            </Button>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Water Meter Reading"
                                type="number"
                                value={waterReading}
                                onChange={(e) => setWaterReading(e.target.value)}
                                disabled={!selectedUnit}
                                InputProps={{
                                    startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                                }}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Electricity Meter Reading"
                                type="number"
                                value={electricityReading}
                                onChange={(e) => setElectricityReading(e.target.value)}
                                disabled={!selectedUnit}
                                InputProps={{
                                    startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                                }}
                            />
                        </Grid>

                        {/* Billing Options */}
                        <Grid item xs={12} sm={6}>
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <Typography>Add Deposit As Arrears</Typography>
                                <Switch
                                    checked={depositAsArrears}
                                    onChange={(e) => setDepositAsArrears(e.target.checked)}
                                />
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                                <Typography>Add Rent As Arrears</Typography>
                                <Switch
                                    checked={rentAsArrears}
                                    onChange={(e) => setRentAsArrears(e.target.checked)}
                                />
                            </Box>
                        </Grid>

                        {/* Next of Kin Details */}
                        <Grid item xs={12}>
                            <Typography variant="h6" sx={{mb: 2, mt: 2}}>Next of Kin Details</Typography>
                            <Divider sx={{mb: 2}}/>
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin First Name"
                                value={formData.next_of_kin_first_name}
                                onChange={handleInputChange('next_of_kin_first_name')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin Last Name"
                                value={formData.next_of_kin_last_name}
                                onChange={handleInputChange('next_of_kin_last_name')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Relationship"
                                value={formData.next_of_kin_relationship}
                                onChange={handleInputChange('next_of_kin_relationship')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin Contacts"
                                value={formData.next_of_kin_contacts}
                                onChange={handleInputChange('next_of_kin_contacts')}
                            />
                        </Grid>

                        <Grid item xs={12} sm={6}>
                            <TextField
                                fullWidth
                                label="Next of Kin National ID"
                                value={formData.next_of_kin_national_id}
                                onChange={handleInputChange('next_of_kin_national_id')}
                            />
                        </Grid>
                    </Grid>
                </DialogContent>

                <DialogActions sx={{p: 3, pt: 1}}>
                    <Button onClick={onClose} color="inherit">
                        Cancel
                    </Button>
                    <LoadingButton
                        onClick={handleSubmit}
                        loading={loading}
                        variant="contained"
                        startIcon={<PersonAddIcon/>}
                        sx={{
                            borderRadius: 2,
                            px: 3,
                            background: `linear-gradient(45deg, ${theme.palette.primary.main}, ${theme.palette.primary.dark})`
                        }}
                    >
                        Add Tenant
                    </LoadingButton>
                </DialogActions>
            </Dialog>

            <DepositManagement
                open={depositModalOpen}
                onClose={() => setDepositModalOpen(false)}
                onSave={handleDepositSave}
            />
        </>
    );
};

// Bill Tenants Dialog Component
const BillTenantsDialog = ({open, onClose, selectedProperty}) => {
    const theme = useTheme();
    const [loading, setLoading] = useState(false);
    const [fetchingTenants, setFetchingTenants] = useState(false);
    const [month, setMonth] = useState(new Date());
    const [tenants, setTenants] = useState([]);
    const [billedTenants, setBilledTenants] = useState([]);
    const [elecBilledTenants, setElecBilledTenants] = useState([]);
    const [htmlContent, setHtmlContent] = useState('');
    const [showBillingConfirmDialog, setShowBillingConfirmDialog] = useState(false);
    const [billingDataForConfirm, setBillingDataForConfirm] = useState(null);

    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    const originalText = safeDecrypt("myMap");

    useEffect(() => {
        if (selectedProperty?.id && open) {
            fetchTenants();
        }
    }, [selectedProperty, open]);

    const fetchTenants = () => {
        setFetchingTenants(true);
        setTenants([]); // Clear previous tenants

        fetch(ApiDetails + "pegasus/visionary/tenant/get/property/tenants", {
            method: "POST",
            headers: {
                "Content-Type": "application/json",
                "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
            },
            body: JSON.stringify({property_id: selectedProperty.id}),
        })
            .then(async (response) => {
                let data = await response.json();
                if (response.status === 200) {
                    setTenants(data);
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed to fetch tenants",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while fetching tenants",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            })
            .finally(() => {
                setFetchingTenants(false);
            });
    };

    const getRowStyle = (params) => ({
        backgroundColor: params.row.billing_enabled ? "transparent" : "#f0f0f0",
        color: params.row.billing_enabled ? "inherit" : "#888",
        pointerEvents: params.row.billing_enabled ? "auto" : "none",
        opacity: params.row.billing_enabled ? 1 : 0.6,
    });

    const handleWaterReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = billedTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalWaterBill = parseFloat(consumedUnits) * parseInt(selectedProperty.water_bill);

        if (finalWaterBill < selectedProperty.minimum_water_bill) {
            finalWaterBill = selectedProperty.minimum_water_bill;
        }

        setBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: parseInt(tenant.arrears),
            finalWaterBill: parseFloat(finalWaterBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalWaterBill),
            meter_reading: value,
            landlord_id: JSON.parse(originalText)[0][1],
        }]);
    };

    const handleElectricityReadingChange = (tenantId, value, tenant) => {
        if (!value) return;

        let filteredTenantArray = elecBilledTenants.filter((t) => t.tenant_id !== tenantId);

        let consumedUnits = value - tenant.electricity_meter_reading;
        consumedUnits = consumedUnits.toFixed(1);

        let finalElecBill = parseFloat(consumedUnits) * parseInt(selectedProperty.electricity_bill);

        if (finalElecBill < selectedProperty.minimum_electricity_bill) {
            finalElecBill = selectedProperty.minimum_electricity_bill;
        }

        setElecBilledTenants([...filteredTenantArray, {
            tenant_id: tenantId,
            rent: parseInt(tenant.rent.toString().replace(",", "")),
            arrears: parseInt(tenant.arrears),
            electricity_bill: parseFloat(finalElecBill),
            total_bill: parseInt(tenant.rent.toString().replace(",", "")) + parseInt(tenant.arrears) + parseFloat(finalElecBill),
            electricity_meter_reading: value,
            landlord_id: JSON.parse(originalText)[0][1],
        }]);
    };

    const billingColumns = [
        {
            field: "user",
            headerName: "Name",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>
                    {params.row.first_name} {params.row.last_name}
                </div>
            ),
        },
        {
            field: "house_number",
            headerName: "Unit",
            flex: 1,
            minWidth: 80,
            renderCell: (params) => (
                <div style={getRowStyle(params)}>{params.row.house_number}</div>
            ),
        },
        {
            field: "rent",
            headerName: "Rent",
            flex: 1,
            minWidth: 100,
            renderCell: (params) => (
                <div style={{...getRowStyle(params), color: "green", fontWeight: 600}}>
                    KSH {parseInt(params.row.rent.toString().replace(",", "")).toLocaleString()}
                </div>
            ),
        },
        {
            field: "meter_reading",
            headerName: "Water Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                    <WaterDropIcon fontSize="small" sx={{color: "#2196F3"}}/>
                    <span>{params.row.meter_reading}</span>
                </Box>
            ),
        },
        {
            field: "current_meter_reading",
            headerName: "New Water Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <TextField
                    type="number"
                    placeholder="Enter Reading..."
                    onChange={(e) => handleWaterReadingChange(params.row.tenant_id, e.target.value, params.row)}
                    disabled={!params.row.billing_enabled}
                    InputProps={{
                        startAdornment: <WaterDropIcon sx={{color: "#2196F3", mr: 1}}/>
                    }}
                    variant="outlined"
                    size="small"
                    sx={{backgroundColor: "white", ...getRowStyle(params)}}
                />
            ),
        },
        {
            field: "electricity_meter_reading",
            headerName: "Electricity Reading",
            flex: 1,
            minWidth: 120,
            renderCell: (params) => (
                <Box sx={{display: "flex", alignItems: "center", gap: 1, ...getRowStyle(params)}}>
                    <ElectricBoltIcon fontSize="small" sx={{color: "#FF9800"}}/>
                    <span>{params.row.electricity_meter_reading}</span>
                </Box>
            ),
        },
        {
            field: "current_electricity_meter_reading",
            headerName: "New Electricity Reading",
            flex: 1,
            minWidth: 150,
            renderCell: (params) => (
                <TextField
                    type="number"
                    placeholder="Enter Reading..."
                    onChange={(e) => handleElectricityReadingChange(params.row.tenant_id, e.target.value, params.row)}
                    disabled={!params.row.billing_enabled}
                    InputProps={{
                        startAdornment: <ElectricBoltIcon sx={{color: "#FF9800", mr: 1}}/>
                    }}
                    variant="outlined"
                    size="small"
                    sx={{backgroundColor: "white", ...getRowStyle(params)}}
                />
            ),
        },
    ];

    const handleBillTenants = async () => {
        /* if (
             billedTenants.length < tenantsLength ||
             elecBilledTenants.length < tenantsLength
         ) {
             showSnackbar({
                 title: "Error!",
                 message: "Please Make sure to bill both water and electricity",
                 type: "error", // Options: success, error, warning, info
                 position: {vertical: "bottom", horizontal: "right"}, // Options for position
             });

             setBillLoading(false);
             return;
         }*/

        // Combine water and electricity bills
        let finalBill = [];

        if (billedTenants.length > elecBilledTenants.length) {
            billedTenants.forEach((tenant) => {
                elecBilledTenants.forEach((tenant2) => {
                    if (tenant.tenant_id === tenant2.tenant_id) {
                        finalBill = [...finalBill, {
                            tenant_id: tenant.tenant_id,
                            water_bill: tenant.finalWaterBill,
                            electricity_bill: tenant2.electricity_bill,
                            total_bill: parseInt(tenant.rent) + parseInt(tenant.arrears) + parseFloat(tenant2.electricity_bill) + parseFloat(tenant.finalWaterBill),
                            meter_reading: tenant.meter_reading,
                            electricity_meter_reading: tenant2.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                        }];
                    }
                });
            });
        } else {
            elecBilledTenants.forEach((tenant) => {
                billedTenants.forEach((tenant2) => {
                    if (tenant.tenant_id === tenant2.tenant_id) {
                        finalBill = [...finalBill, {
                            tenant_id: tenant.tenant_id,
                            water_bill: tenant2.finalWaterBill,
                            electricity_bill: tenant.electricity_bill,
                            total_bill: parseInt(tenant.rent) + parseInt(tenant.arrears) + parseFloat(tenant.electricity_bill) + parseFloat(tenant2.finalWaterBill),
                            meter_reading: tenant2.meter_reading,
                            electricity_meter_reading: tenant.electricity_meter_reading,
                            landlord_id: JSON.parse(originalText)[0][1],
                        }];
                    }
                });
            });
        }

        // Add water-only bills
        let finalWaterBills = [];
        billedTenants.forEach((reading) => {
            const hasElectricity = elecBilledTenants.some(elec => elec.tenant_id === reading.tenant_id);
            if (!hasElectricity) {
                finalWaterBills.push({
                    tenant_id: reading.tenant_id,
                    water_bill: reading.finalWaterBill,
                    electricity_bill: 0,
                    total_bill: parseInt(reading.rent) + parseInt(reading.arrears) + parseFloat(reading.finalWaterBill),
                    meter_reading: reading.meter_reading,
                    electricity_meter_reading: 0,
                    landlord_id: JSON.parse(originalText)[0][1],
                });
            }
        });

        // Add electricity-only bills
        let finalElectricityBills = [];
        elecBilledTenants.forEach((reading) => {
            const hasWater = billedTenants.some(water => water.tenant_id === reading.tenant_id);
            if (!hasWater) {
                finalElectricityBills.push({
                    tenant_id: reading.tenant_id,
                    water_bill: 0,
                    electricity_bill: reading.electricity_bill,
                    total_bill: parseInt(reading.rent) + parseInt(reading.arrears) + parseFloat(reading.electricity_bill),
                    meter_reading: 0,
                    electricity_meter_reading: reading.electricity_meter_reading,
                    landlord_id: JSON.parse(originalText)[0][1],
                });
            }
        });

        finalBill = [...finalBill, ...finalWaterBills, ...finalElectricityBills];

        if (!month) {
            showSnackbar({
                title: "Error!",
                message: "Kindly select the month you are billing for.",
                type: "error", // Options: success, error, warning, info
                position: {vertical: "bottom", horizontal: "right"}, // Options for position
            });
            setLoading(false); // Reset loading state while waiting for confirmation
            return;
        }

        // Calculate estimated total for the confirmation dialog
        const estimatedTotal = finalBill.reduce((sum, bill) => sum + (bill.total_bill || 0), 0);

        // Prepare billing data for confirmation dialog
        const billingConfirmData = {
            month: getMonthName(parseInt(month.getMonth())).toUpperCase(),
            year: parseInt(month.getFullYear()),
            propertyName: selectedProperty.property_name,
            tenantCount: finalBill.length,
            estimatedTotal: estimatedTotal
        };

        // Store the final bill data for later use
        setBillingDataForConfirm({
            finalBill,
            billingConfirmData
        });

        // Show the confirmation dialog instead of the basic confirm()
        setShowBillingConfirmDialog(true);
        setLoading(false); // Reset loading state while waiting for confirmation
    };

    // New function to handle confirmation
    const handleBillingConfirm = async () => {
        setShowBillingConfirmDialog(false);
        setLoading(true);


        const {finalBill} = billingDataForConfirm;

        // Make Billing API Call
        try {
            const response = await fetch(ApiDetails + "pegasus/visionary/property/bill/tenants", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": "8d5b2e7c-36f4-4b45-b79b-61fc60eb905d"
                },
                body: JSON.stringify([
                    ...finalBill,
                    {
                        month: parseInt(month.getMonth()),
                        year: parseInt(month.getFullYear()),
                        property_id: selectedProperty.id,
                        property_name: selectedProperty.property_name,
                    },
                ]),
            });

            if (response.status === 200) {
                const data = await response.json();
                setHtmlContent(data.htmlReport);

                showSnackbar({
                    title: "Success!",
                    message: "Tenants billed successfully.",
                    type: "success",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Failed Billing tenants.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
                setLoading(false);
            }
        } catch (err) {
            console.log(err);
            showSnackbar({
                title: "Error!",
                message: "Something Went Wrong. Call Customer Care For Assistance.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
        }
    };

// New function to handle cancellation
    const handleBillingCancel = async () => {
        setShowBillingConfirmDialog(false);
        setLoading(false);
    };

    const downloadPDF = () => {
        fetch(ApiDetails + "pegasus/visionary/property/bill/report", {
            method: "POST",
            body: JSON.stringify({pdf: htmlContent}),
        })
            .then(async (response) => {
                if (response.status === 200) {
                    showSnackbar({
                        title: "Success!",
                        message: "File download will start shortly",
                        type: "success",
                        position: {vertical: "bottom", horizontal: "right"},
                    });

                    let blob = await response.blob();
                    let href = window.URL.createObjectURL(blob);
                    const link = document.createElement("a");
                    link.href = href;
                    link.setAttribute("download", "billing_report.pdf");
                    document.body.appendChild(link);
                    link.click();
                    document.body.removeChild(link);
                    URL.revokeObjectURL(href);
                    onClose();
                } else {
                    showSnackbar({
                        title: "Error!",
                        message: "Failed downloading report",
                        type: "error",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            })
            .catch((err) => {
                console.log(err);
                showSnackbar({
                    title: "Error!",
                    message: "Something went wrong while downloading report",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            });
    };

    return (
        <>

            {/*Standard Billing Dialog*/}
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        overflow: 'visible'
                    }
                }}
            >
                <DialogTitle sx={{
                    pb: 1,
                    background: `linear-gradient(45deg, ${alpha(theme.palette.success.main, 0.1)}, ${alpha(theme.palette.success.light, 0.05)})`,
                    position: 'relative'
                }}>
                    <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                        <Box sx={{display: 'flex', alignItems: 'center'}}>
                            <Box
                                sx={{
                                    backgroundColor: alpha(theme.palette.success.main, 0.1),
                                    p: 1,
                                    borderRadius: 2,
                                    mr: 2,
                                    color: theme.palette.success.main
                                }}
                            >
                                <ReceiptLongIcon/>
                            </Box>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    Bill Property Tenants
                                </Typography>
                                {selectedProperty && (
                                    <Typography variant="body2" color="text.secondary">
                                        {selectedProperty.property_name} • {selectedProperty.location}
                                    </Typography>
                                )}
                            </Box>
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon/>
                        </IconButton>
                    </Box>
                </DialogTitle>

                <DialogContent sx={{pt: 3}}>
                    {htmlContent !== "" ? (
                        <Box sx={{
                            borderRadius: '12px',
                            border: '1px solid #e0e0e0',
                            padding: '24px',
                            backgroundColor: '#fff',
                            boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                            width: '100%'
                        }}>
                            <div dangerouslySetInnerHTML={{__html: htmlContent}}/>
                        </Box>
                    ) : (
                        <Box>
                            {/* Billing Month Selection */}
                            <Box sx={{
                                p: 3,
                                backgroundColor: "#f7f9fc",
                                borderRadius: 2,
                                mb: 3,
                                border: "1px solid #e0e7ff"
                            }}>
                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 2}}>
                                            <CalendarMonthIcon color="primary"/>
                                            <Typography variant="subtitle1" sx={{fontWeight: 600}}>
                                                Select Billing Month
                                            </Typography>
                                        </Box>

                                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                                            <DatePicker
                                                views={["year", "month"]}
                                                value={month}
                                                onChange={(newValue) => setMonth(newValue)}
                                                renderInput={(params) => (
                                                    <TextField
                                                        {...params}
                                                        fullWidth
                                                        variant="outlined"
                                                        label=""
                                                    />
                                                )}
                                            />
                                        </LocalizationProvider>
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <Box sx={{
                                            p: 2,
                                            backgroundColor: "#e3f2fd",
                                            borderRadius: 2,
                                            border: "1px solid #bbdefb"
                                        }}>
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mb: 1}}>
                                                <InfoIcon color="info"/>
                                                <Typography variant="subtitle2" sx={{fontWeight: 600}}>
                                                    Billing Information
                                                </Typography>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                                                <WaterDropIcon fontSize="small" color="info"/>
                                                <Typography variant="body2">
                                                    Water rate: <strong>{selectedProperty?.water_bill} KSH</strong> per
                                                    unit
                                                    (min: {selectedProperty?.minimum_water_bill} KSH)
                                                </Typography>
                                            </Box>

                                            <Box sx={{display: "flex", alignItems: "center", gap: 1, mt: 1}}>
                                                <ElectricBoltIcon fontSize="small" color="warning"/>
                                                <Typography variant="body2">
                                                    Electricity
                                                    rate: <strong>{selectedProperty?.electricity_bill} KSH</strong> per
                                                    unit
                                                    (min: {selectedProperty?.minimum_electricity_bill} KSH)
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Box>

                            {/* Tenants DataGrid */}
                            <Box sx={{height: 500, width: '100%', position: 'relative'}}>
                                {fetchingTenants ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <CircularProgress
                                            size={60}
                                            thickness={4}
                                            sx={{
                                                color: theme.palette.primary.main,
                                                mb: 2
                                            }}
                                        />
                                        <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                                            Loading Tenants
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            Fetching tenant data for {selectedProperty?.property_name}...
                                        </Typography>
                                    </Box>
                                ) : tenants.length === 0 ? (
                                    <Box sx={{
                                        display: 'flex',
                                        flexDirection: 'column',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        backgroundColor: '#f9f9f9',
                                        borderRadius: 2,
                                        border: '1px solid #e0e0e0'
                                    }}>
                                        <PeopleAltIcon
                                            sx={{
                                                fontSize: 60,
                                                color: theme.palette.text.disabled,
                                                mb: 2
                                            }}
                                        />
                                        <Typography variant="h6" color="text.secondary" sx={{mb: 1}}>
                                            No Tenants Found
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            There are no tenants in this property yet.
                                        </Typography>
                                    </Box>
                                ) : (
                                    <StyledDataGrid
                                        rows={tenants}
                                        columns={billingColumns}
                                        disableSelectionOnClick
                                        initialState={{
                                            sorting: {sortModel: [{field: "house_number", sort: "asc"}]},
                                            pagination: {paginationModel: {pageSize: 10}}
                                        }}
                                        rowsPerPageOptions={[10]}
                                        sx={{
                                            border: "1px solid #e0e0e0",
                                            borderRadius: "8px",
                                            boxShadow: "0 2px 8px rgba(0,0,0,0.05)"
                                        }}
                                    />
                                )}
                            </Box>
                        </Box>
                    )}
                </DialogContent>

                <DialogActions sx={{p: 3, pt: 1}}>
                    {htmlContent !== "" ? (
                        <>
                            <Button
                                variant="contained"
                                startIcon={<SendIcon/>}
                                onClick={downloadPDF}
                                sx={{
                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                }}
                            >
                                Download PDF
                            </Button>
                            <Button onClick={() => window.location.reload()} color="inherit">
                                Close
                            </Button>
                        </>
                    ) : (
                        <>
                            <Button onClick={onClose} color="inherit">
                                Cancel
                            </Button>
                            <LoadingButton
                                onClick={handleBillTenants}
                                loading={loading}
                                disabled={fetchingTenants || tenants.length === 0}
                                variant="contained"
                                startIcon={<SendIcon/>}
                                sx={{
                                    borderRadius: 2,
                                    px: 3,
                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`
                                }}
                            >
                                Send Bills
                            </LoadingButton>
                        </>
                    )}
                </DialogActions>
            </Dialog>

            <CardBillingConfirmationDialog
                isOpen={showBillingConfirmDialog}
                onConfirm={handleBillingConfirm}
                onCancel={handleBillingCancel}
                billingData={billingDataForConfirm?.billingConfirmData || {}}
            />
        </>
    );
};
// Property metrics card component
const PropertyMetricsCard = ({property, metrics, properties, onPropertyChange}) => {
    const theme = useTheme();

    // Format currency for better readability
    const formatCurrency = (value) => {
        return new Intl.NumberFormat('en-KE', {
            style: 'currency',
            currency: 'KES',
            minimumFractionDigits: 1,
            maximumFractionDigits: 1
        }).format(value);
    };

    // Format numbers with one decimal place
    const formatNumber = (value) => {
        return value.toFixed(1);
    };

    return (
        <Card
            elevation={0}
            sx={{
                height: '100%',
                borderRadius: 2,
                border: `1px solid ${theme.palette.divider}`,
                overflow: 'visible'
            }}
        >
            <CardContent sx={{p: 3}}>
                <Box sx={{display: 'flex', alignItems: 'center', mb: 3}}>
                    <Box
                        sx={{
                            backgroundColor: alpha(theme.palette.primary.main, 0.1),
                            p: 1.5,
                            borderRadius: 2,
                            mr: 2,
                            color: theme.palette.primary.main
                        }}
                    >
                        <HomeWorkIcon fontSize="large"/>
                    </Box>
                    <FormControl fullWidth variant="standard" sx={{minWidth: 120}}>
                        <Select
                            value={property?.id || ''}
                            onChange={onPropertyChange}
                            IconComponent={KeyboardArrowDownIcon}
                            displayEmpty
                            renderValue={() => (
                                <Box>
                                    <Typography variant="h6" fontWeight="bold" sx={{mb: 0.5}}>
                                        {property.property_name}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        {property.location}
                                    </Typography>
                                </Box>
                            )}
                            sx={{
                                '.MuiSelect-select': {
                                    paddingY: 0,
                                    paddingX: 0,
                                    border: 'none'
                                },
                                '&:before, &:after': {
                                    display: 'none'
                                }
                            }}
                        >
                            {properties.map((prop) => (
                                <MenuItem key={prop.id} value={prop.id}>
                                    <Box>
                                        <Typography variant="subtitle1" fontWeight="medium">
                                            {prop.property_name}
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary">
                                            {prop.location}
                                        </Typography>
                                    </Box>
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Box>

                <Divider sx={{my: 2}}/>

                <Grid container spacing={2}>
                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Income Due
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium">
                                {formatCurrency(metrics.expectedIncome)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Income Collected
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium" color="success.main">
                                {formatCurrency(metrics.receivedIncome)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Expenses
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium" color="error.main">
                                {formatCurrency(metrics.expenseCost)}
                            </Typography>
                        </Box>
                    </Grid>

                    <Grid item xs={6}>
                        <Box sx={{mt: 2}}>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 0.5}}>
                                Total Refundable Deposit
                            </Typography>
                            <Typography variant="subtitle1" fontWeight="medium">
                                {formatCurrency(metrics.depositHeld)}
                            </Typography>
                        </Box>
                    </Grid>
                </Grid>
            </CardContent>
        </Card>
    );
};

const EnhancedProgressBars = () => {
    const theme = useTheme();

    // Safe decryption function
    const safeDecrypt = (key) => {
        try {
            return CryptoJS.AES.decrypt(localStorage.getItem(key), KEY).toString(CryptoJS.enc.Utf8) || "";
        } catch {
            return "";
        }
    };

    // Retrieve decrypted data from localStorage
    const [originalText2, setOriginalText2] = useState(safeDecrypt("portfolioDetails"));
    const [parsedData, setParsedData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [properties, setProperties] = useState([]);
    const [selectedProperty, setSelectedProperty] = useState(null);
    const [addTenantOpen, setAddTenantOpen] = useState(false);
    const [billTenantsOpen, setBillTenantsOpen] = useState(false);
    const [metrics, setMetrics] = useState({
        totalUnits: 0,
        occupiedUnits: 0,
        vacantUnits: 0,
        expectedIncome: 0,
        receivedIncome: 0,
        tax: 0,
        depositHeld: 0,
        expenseCost: 0,
        profit: 0,
        incomePercentage: 0,
        occupancyPercentage: 0,
        profitPercentage: 0,
    });

    // Format the data
    useEffect(() => {
        try {
            if (originalText2) {
                const parsed = JSON.parse(originalText2);
                if (!parsed || !Array.isArray(parsed) || !parsed[1]?.[1]) {
                    throw new Error("Invalid portfolio details structure");
                }
                setParsedData(parsed);
                const propertiesList = parsed[1][1] || [];
                setProperties(propertiesList);
                if (propertiesList.length > 0) {
                    setSelectedProperty(propertiesList[0]);
                } else {
                    showSnackbar({
                        title: "Warning!",
                        message: "No properties found. Please add a property to continue.",
                        type: "warning",
                        position: {vertical: "bottom", horizontal: "right"},
                    });
                }
            } else {
                showSnackbar({
                    title: "Error!",
                    message: "Portfolio details not found. Please log in again.",
                    type: "error",
                    position: {vertical: "bottom", horizontal: "right"},
                });
            }
            setLoading(false);
        } catch (error) {
            console.error("Error parsing data:", error);
            showSnackbar({
                title: "Error!",
                message: "Failed to load portfolio details. Please try again or contact support.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            setLoading(false);
        }
    }, [originalText2]);
    // Calculate metrics when selected property changes
    useEffect(() => {
        if (!parsedData || !selectedProperty) return;

        // Extract the maps
        const expectedIncomeMap = parsedData[8][1] || {};
        const receivedIncomeMap = parsedData[9][1] || {};
        const taxMap = parsedData[16][1] || {};
        const depositHeldMap = parsedData[17][1] || {};
        const expenseCostMap = parsedData[19][1] || {};
        const occupancyMap = parsedData[20][1] || {};
        const occupiedUnitsMap = parsedData[21][1] || {};
        const vacantUnitsMap = parsedData[22][1] || {};

        const propertyId = selectedProperty.id;

        // Get the metrics for the selected property
        const vacantUnits = vacantUnitsMap[propertyId] || 0;
        const occupiedUnits = occupiedUnitsMap[propertyId] || 0;
        const totalUnits = vacantUnits + occupiedUnits;
        const expectedIncome = expectedIncomeMap[propertyId] || 0;
        const receivedIncome = receivedIncomeMap[propertyId] || 0;
        const tax = taxMap[propertyId] || 0;
        const depositHeld = depositHeldMap[propertyId] || 0;
        const expenseCost = expenseCostMap[propertyId] || 0;
        const profit = receivedIncome - expenseCost;
        const occupancy = occupancyMap[propertyId] || 0;

        // Calculate percentages
        const incomePercentage = Math.round((receivedIncome / (expectedIncome || 1)) * 100);
        const profitPercentage = Math.round((profit / (receivedIncome || 1)) * 100);

        // Progressive animation for the metrics
        setMetrics({
            totalUnits,
            occupiedUnits,
            vacantUnits,
            expectedIncome,
            receivedIncome,
            tax,
            depositHeld,
            expenseCost,
            profit,
            incomePercentage: 0,
            occupancyPercentage: 0,
            profitPercentage: 0,
        });

        // Animate the progress bars
        const timer1 = setTimeout(() => {
            setMetrics(prev => ({...prev, incomePercentage}));
        }, 300);

        const timer2 = setTimeout(() => {
            setMetrics(prev => ({...prev, occupancyPercentage: occupancy}));
        }, 600);

        const timer3 = setTimeout(() => {
            setMetrics(prev => ({...prev, profitPercentage}));
        }, 900);

        return () => {
            clearTimeout(timer1);
            clearTimeout(timer2);
            clearTimeout(timer3);
        };
    }, [selectedProperty, parsedData]);

    // Handle property change
    const handlePropertyChange = (event) => {
        const propertyId = parseInt(event.target.value, 10);
        const property = properties.find((prop) => prop.id === propertyId);
        if (property) {
            setSelectedProperty(property);
        }
    };

// Format number with one decimal place
    const formatNumberWithDecimals = (value) => {
        return Number(value).toFixed(1);
    };

// Action handlers
    const handleAddTenant = () => {
        setAddTenantOpen(true);
    };

    const handleBillTenants = () => {
        if (!selectedProperty || !selectedProperty.id) {
            showSnackbar({
                title: "Error!",
                message: "No property selected. Please select a property to bill tenants.",
                type: "error",
                position: {vertical: "bottom", horizontal: "right"},
            });
            return;
        }
        setBillTenantsOpen(true);
    };

    return (
        <Box sx={{pb: 2}}>

            <Grid container spacing={3}>

                {/* Right Side - Property Details */}
                <Grid item xs={12} lg={4}>
                    {selectedProperty && (
                        <PropertyMetricsCard
                            property={selectedProperty}
                            metrics={metrics}
                            properties={properties}
                            onPropertyChange={handlePropertyChange}
                        />
                    )}
                </Grid>

                {/* Left Side - Progress Metrics and Unit Stats */}
                <Grid item xs={12} lg={8}>
                    <Grid container spacing={3}>
                        {/* Performance Metrics */}
                        <Grid item xs={12}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Box sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    mb: 3
                                }}>
                                    <Typography variant="h6" fontWeight="bold">
                                        Performance Metrics
                                    </Typography>

                                    {/* Integrated Quick Actions */}
                                    {selectedProperty && (
                                        <Box sx={{display: 'flex', gap: 1}}>
                                            <Button
                                                variant="outlined"
                                                startIcon={<PersonAddIcon/>}
                                                onClick={handleAddTenant}
                                                size="small"
                                                sx={{
                                                    borderColor: alpha(theme.palette.primary.main, 0.3),
                                                    color: theme.palette.primary.main,
                                                    '&:hover': {
                                                        borderColor: theme.palette.primary.main,
                                                        backgroundColor: alpha(theme.palette.primary.main, 0.08)
                                                    }
                                                }}
                                            >
                                                Add Tenant
                                            </Button>
                                            <Button
                                                variant="contained"
                                                startIcon={<ReceiptLongIcon/>}
                                                onClick={handleBillTenants}
                                                size="small"
                                                disabled={!selectedProperty || !selectedProperty.id}
                                                sx={{
                                                    background: `linear-gradient(45deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                                                    boxShadow: `0 2px 8px ${alpha(theme.palette.success.main, 0.3)}`,
                                                    '&:hover': {
                                                        boxShadow: `0 4px 12px ${alpha(theme.palette.success.main, 0.4)}`
                                                    }
                                                }}
                                            >
                                                Bill Tenants
                                            </Button>
                                        </Box>
                                    )}
                                </Box>

                                <Grid container spacing={3}>
                                    <Grid item xs={12} md={6}>
                                        <ProgressCircle
                                            value={metrics.incomePercentage}
                                            text={`${formatNumberWithDecimals(metrics.incomePercentage)}%`}
                                            label="Collection Rate"
                                            // description={`KSH ${formatNumberWithDecimals(metrics.receivedIncome)} of ${formatNumberWithDecimals(metrics.expectedIncome)}`}
                                            description={``}
                                            icon={<AttachMoneyIcon/>}
                                            color={theme.palette.success.main}
                                        />
                                    </Grid>

                                    <Grid item xs={12} md={6}>
                                        <ProgressCircle
                                            value={metrics.occupancyPercentage}
                                            text={`${formatNumberWithDecimals(metrics.occupancyPercentage)}%`}
                                            label="Occupancy Rate"
                                            // description={`${formatNumberWithDecimals(metrics.occupiedUnits)} of ${formatNumberWithDecimals(metrics.totalUnits)} units occupied`}
                                            description={``}
                                            icon={<PeopleAltIcon/>}
                                            color={theme.palette.info.main}
                                        />
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>

                        {/* Unit Statistics */}
                        <Grid item xs={12}>
                            <Card
                                elevation={0}
                                sx={{
                                    p: 3,
                                    borderRadius: 2,
                                    border: `1px solid ${theme.palette.divider}`,
                                }}
                            >
                                <Typography variant="h6" fontWeight="bold" sx={{mb: 3}}>
                                    Unit Statistics
                                </Typography>

                                <Grid container spacing={3}>
                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <MeetingRoomIcon
                                                    sx={{color: theme.palette.primary.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Total Units
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold">
                                                {formatNumberWithDecimals(metrics.totalUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <PeopleAltIcon
                                                    sx={{color: theme.palette.success.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Occupied
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="success.main">
                                                {formatNumberWithDecimals(metrics.occupiedUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <MeetingRoomIcon
                                                    sx={{color: theme.palette.error.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Vacant
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="error.main">
                                                {formatNumberWithDecimals(metrics.vacantUnits)}
                                            </Typography>
                                        </Box>
                                    </Grid>

                                    <Grid item xs={6} sm={3}>
                                        <Box sx={{textAlign: 'center'}}>
                                            <Box sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                mb: 1
                                            }}>
                                                <PercentIcon
                                                    sx={{color: theme.palette.info.main, mr: 1, fontSize: 20}}/>
                                                <Typography variant="body2" color="text.secondary">
                                                    Occupancy
                                                </Typography>
                                            </Box>
                                            <Typography variant="h5" fontWeight="bold" color="info.main">
                                                {formatNumberWithDecimals(metrics.occupancyPercentage)}%
                                            </Typography>
                                        </Box>
                                    </Grid>
                                </Grid>
                            </Card>
                        </Grid>
                    </Grid>
                </Grid>
            </Grid>

            {/* Dialogs */}
            <AddTenantDialog
                open={addTenantOpen}
                onClose={() => setAddTenantOpen(false)}
                selectedProperty={selectedProperty}
            />

            <BillTenantsDialog
                open={billTenantsOpen}
                onClose={() => setBillTenantsOpen(false)}
                selectedProperty={selectedProperty}
            />
        </Box>
    );
};

export default EnhancedProgressBars;